/**
 * Sipariş (PO) ve mal kabulü (GRN) ekranlarının metinleri (M12c; ADR 0040). Ekran adları
 * tasarımdaki gibi İngilizce (`satin-alma-akisi.jpeg` "Purchase Order", "Delivery & GRN";
 * `gemi-satin-alma.jpeg` 3, 6, 11); Türkçe ikinci dil. PDF metinleri tedarikçiye giden belgenindir.
 */

export const orderErrorsEn = {
  RFQ_NOT_SELECTED: 'The RFQ has no selected supplier.',
  PO_EXISTS: 'This RFQ already has a purchase order.',
  PO_FIXED: 'Items and prices of a purchase order cannot be changed.',
  PO_LOCKED: 'A submitted or approved purchase order cannot be changed.',
  PO_CLOSED: 'The purchase order is cancelled or completed.',
  PO_NOT_ISSUED: 'The purchase order is not approved yet.',
  PO_ALREADY_SENT: 'The purchase order is already sent.',
  PO_ALREADY_CONFIRMED: 'The supplier confirmation is already recorded.',
  PO_NOT_CLOSABLE:
    'Only a purchase order with goods receipts can be closed. An order with no receipt is cancelled.',
  CLOSE_REASON_REQUIRED: 'Part of the order is still open. Enter the reason for closing.',
  PO_NOT_RECEIVABLE: 'This purchase order cannot be received (not approved, cancelled or closed).',
  PO_HAS_RECEIPTS: 'Goods were received against this order; it cannot be cancelled.',
  MAIL_NOT_CONFIGURED:
    'E-mail is not configured. Print or download the PO, send it yourself and mark it as sent.',
  SUPPLIER_EMAIL_MISSING: 'The supplier has no e-mail address. Add it on the supplier card.',
  EMAIL_PENDING: 'An e-mail to the supplier is already queued.',
  EMAIL_LIMIT: 'This order was sent too many times today. Try again tomorrow.',
  INVALID_PURCHASE_ORDER: 'Some fields are invalid.',
  OVER_RECEIPT: 'The quantities exceed what is still open on the order.',
  EMPTY_RECEIPT: 'Enter at least one received, damaged or missing quantity.',
  RECEIPT_LINES_INCOMPLETE: 'The order changed meanwhile. Reopen the form and try again.',
  UNKNOWN_PO_LINE: 'The order items changed. Reopen the form and try again.',
  CONDITION_MISMATCH: 'The condition does not match the quantities.',
  INVALID_GOODS_RECEIPT: 'Some fields are invalid.',
  RECEIVER_NOT_ON_VESSEL: 'The receiver is not on board on that day.',
  RECEIVED_BEFORE_ORDER: 'The received date cannot be before the order date.',
  GRN_CANCELLED: 'The GRN is already cancelled.',
  NEGATIVE_STOCK: 'The received material has been used; the GRN cannot be cancelled.',
};

export const orderErrorsTr: typeof orderErrorsEn = {
  RFQ_NOT_SELECTED: 'Teklif isteğinde seçilmiş tedarikçi yok.',
  PO_EXISTS: 'Bu teklif isteğinin siparişi zaten var.',
  PO_FIXED: 'Siparişin kalemleri ve fiyatları değiştirilemez.',
  PO_LOCKED: 'Onaya gönderilmiş ya da onaylanmış sipariş değiştirilemez.',
  PO_CLOSED: 'Sipariş iptal edildi ya da tamamlandı.',
  PO_NOT_ISSUED: 'Sipariş henüz onaylanmadı.',
  PO_ALREADY_SENT: 'Sipariş zaten gönderildi.',
  PO_ALREADY_CONFIRMED: 'Tedarikçi teyidi zaten kayıtlı.',
  PO_NOT_CLOSABLE:
    'Yalnız mal kabulü girilmiş sipariş kapatılır. Mal kabulü olmayan sipariş iptal edilir.',
  CLOSE_REASON_REQUIRED: 'Siparişin bir kısmı hâlâ açık. Kapatma gerekçesini yazın.',
  PO_NOT_RECEIVABLE: 'Bu sipariş teslim alınamaz (onaylı değil, iptal ya da kapalı).',
  PO_HAS_RECEIPTS: 'Bu siparişe mal kabulü girildi; iptal edilemez.',
  MAIL_NOT_CONFIGURED:
    "E-posta ayarlı değil. PO'yu yazdırın ya da indirin, kendiniz gönderin ve gönderildi olarak işaretleyin.",
  SUPPLIER_EMAIL_MISSING: 'Tedarikçinin e-posta adresi yok. Tedarikçi kartına ekleyin.',
  EMAIL_PENDING: 'Tedarikçiye giden bir e-posta zaten kuyrukta.',
  EMAIL_LIMIT: 'Bu sipariş bugün çok kez gönderildi. Yarın yeniden deneyin.',
  INVALID_PURCHASE_ORDER: 'Bazı alanlar geçersiz.',
  OVER_RECEIPT: 'Miktarlar siparişte açık kalanı aşıyor.',
  EMPTY_RECEIPT: 'En az bir kabul edilen, hasarlı ya da eksik miktar girin.',
  RECEIPT_LINES_INCOMPLETE: 'Sipariş bu arada değişti. Formu yeniden açıp deneyin.',
  UNKNOWN_PO_LINE: 'Siparişin kalemleri değişti. Formu yeniden açıp deneyin.',
  CONDITION_MISMATCH: 'Kalite sonucu miktarlarla uyuşmuyor.',
  INVALID_GOODS_RECEIPT: 'Bazı alanlar geçersiz.',
  RECEIVER_NOT_ON_VESSEL: 'Teslim alan o gün gemide görevli değil.',
  RECEIVED_BEFORE_ORDER: 'Teslim günü sipariş gününden önce olamaz.',
  GRN_CANCELLED: 'GRN zaten iptal edildi.',
  NEGATIVE_STOCK: 'Teslim alınan malzeme kullanıldı; GRN iptal edilemez.',
};

export const poEn = {
  // Ortak kodların siparişe özgü metni (M12c-R20); yoksa `purchasing.errors`
  errors: {
    ROW_VERSION_CONFLICT: 'Someone else changed this purchase order. Reopen it and try again.',
    CONTENT_CHANGED: 'The purchase order changed meanwhile. Reopen it and try again.',
    NOT_PENDING: 'The purchase order is not waiting for approval.',
    SELF_APPROVAL_FORBIDDEN: 'You cannot approve a purchase order you created or submitted.',
  },
  statuses: {
    draft: 'Draft',
    pending_approval: 'Pending Approval',
    issued: 'Issued',
    sent: 'Sent to Supplier',
    confirmed: 'Confirmed',
    partially_delivered: 'Partially Delivered',
    delivered: 'Delivered',
    completed: 'Completed',
    cancelled: 'Cancelled',
  },
  list: {
    title: 'Purchase Orders',
    descriptionWeb: 'Orders to suppliers from the selected quotations, with approval and delivery.',
    descriptionShip:
      'Purchase orders of the vessel issued by the office. Receive the goods with a GRN.',
    new: 'New PO',
    filters: 'Purchase order filters',
    search: 'Search',
    searchPlaceholder: 'Search by PO No, supplier…',
    vessel: 'Vessel',
    allVessels: 'All Vessels',
    status: 'Status',
    allStatuses: 'All Statuses',
    dateRange: 'Date Range',
    dateFrom: 'Order date from',
    dateTo: 'Order date to',
    table: 'Purchase orders',
    columns: {
      poNo: 'PO No',
      date: 'Date',
      supplier: 'Supplier',
      vessel: 'Vessel',
      amount: 'Amount',
      status: 'Status',
      actions: 'Actions',
    },
    open: 'Open {{no}}',
    showing: 'Showing {{from}}-{{to}} of {{total}}',
    pagination: 'Purchase order pages',
    emptyTitle: 'No purchase orders yet.',
    emptyWeb: 'Create a purchase order from an RFQ with a selected supplier.',
    emptyShip: 'Purchase orders issued by the office appear here after synchronisation.',
    noMatch: 'No purchase order matches the filters.',
    error: 'The purchase orders could not be loaded.',
    receivedAfterCancel: 'Received after cancellation',
    receivedAfterClose: 'Changed after closing',
  },
  new: {
    title: 'New Purchase Order',
    body: 'Choose an RFQ with a selected supplier. The items and prices are copied from the selected quotation.',
    rfq: 'RFQ',
    option: '{{rfq}} · {{supplier}} · {{total}} {{currency}}',
    none: 'There is no RFQ with a selected supplier waiting for a purchase order.',
    create: 'Create PO',
  },
  detail: {
    title: 'Purchase Order',
    description: 'Order to the supplier: approval, sending and deliveries.',
    loading: 'Loading the purchase order…',
    error: 'The purchase order could not be loaded.',
    supplier: 'Supplier',
    currency: 'Currency',
    totalAmount: 'Total Amount',
    expectedDelivery: 'Expected Delivery',
    print: 'Print',
    download: 'Download',
    send: 'Send to Supplier',
    resend: 'Send Again',
    markSent: 'Mark as Sent',
    submit: 'Submit for Approval',
    approve: 'Approve',
    return: 'Return',
    edit: 'Edit Delivery',
    confirm: 'Supplier Confirmed',
    cancel: 'Cancel PO',
    close: 'Close PO',
    receive: 'Receive (GRN)',
    back: 'Back to purchase orders',
    tabsLabel: 'Purchase order sections',
    tabs: {
      items: 'Items',
      delivery: 'Delivery Details',
      receipts: 'Receipts ({{count}})',
      history: 'History',
    },
    columns: {
      no: '#',
      material: 'Material',
      qty: 'Qty',
      uom: 'UOM',
      unitPrice: 'Unit Price',
      total: 'Total',
      received: 'Received',
      open: 'Open',
    },
    total: 'Total',
    itemsTotal: 'Items',
    discount: 'Discount',
    freight: 'Freight',
    customs: 'Customs',
    tax: 'Tax',
    serviceFee: 'Service Fee',
    received: 'Received',
    remaining: 'Remaining',
    facts: {
      poNo: 'PO No',
      date: 'Date',
      vessel: 'Vessel',
      requisition: 'Requisition',
      rfq: 'RFQ',
      department: 'Department',
      requestedBy: 'Requested By',
      deliveryLocation: 'Delivery Location',
      deliveryTerms: 'Delivery Terms',
      paymentTerms: 'Payment Terms',
      warranty: 'Warranty',
      supplierRef: 'Supplier Confirmation No',
      remarks: 'Remarks',
      contact: 'Contact',
      email: 'E-mail',
    },
    openRequisition: 'Open {{no}}',
    openRfq: 'Open {{no}}',
    receiptsEmpty: 'No goods received yet.',
    receiptsEmptyText: 'Receipts are entered on board against this order.',
    receiptColumns: {
      grnNo: 'GRN No',
      date: 'Date',
      receivedBy: 'Received By',
      deliveryNote: 'Delivery Note',
      accepted: 'Accepted',
      status: 'Status',
    },
    receiptCancelled: 'Cancelled',
    receiptValid: 'Received',
    emails: 'E-mails to the supplier',
    emailColumns: { to: 'To', queuedAt: 'Requested', status: 'Status', attempts: 'Attempts' },
    emailStatus: {
      queued: 'Queued',
      sending: 'Sending',
      sent: 'Sent',
      failed: 'Failed',
      skipped: 'Skipped',
    },
    events: {
      created: 'Purchase order created',
      submitted: 'Submitted for approval',
      returned: 'Returned to draft',
      approved: 'Approved and issued',
      sent: 'Sent to the supplier',
      confirmed: 'Confirmed by the supplier',
      cancelled: 'Cancelled',
      closed: 'Closed',
    },
    sentVia: { email: 'by e-mail', manual: 'marked as sent' },
    noHistory: 'No history yet.',
    draftNote: 'Draft: check the delivery details and submit the order for approval.',
    pendingNote: 'Waiting for approval by another person with purchasing approval rights.',
    cancelledNote: 'Cancelled: {{reason}}',
    closedNote: 'Completed: {{reason}}',
    closedNoReason: 'Completed.',
    receivedAfterCancel_one:
      'Goods were received on board after the cancellation ({{count}} receipt). Check with the vessel.',
    receivedAfterCancel_other:
      'Goods were received on board after the cancellation ({{count}} receipts). Check with the vessel.',
    receivedAfterClose_one:
      'A goods receipt was entered or cancelled on board after the order was closed. Check with the vessel.',
    receivedAfterClose_other:
      '{{count}} goods receipts were entered or cancelled on board after the order was closed. Check with the vessel.',
    mailOff:
      'E-mail is not configured. Print or download the PO, send it to the supplier yourself and mark it as sent.',
    emailPending:
      'An e-mail to the supplier is queued. The order can be sent again after it goes out.',
    readOnly: 'Purchase orders are issued in the office; on board they are read only.',
    submitTitle: 'Submit {{no}} for approval',
    submitBody:
      'The submission is recorded in your name. The order is locked until it is approved or returned.',
    approveTitle: 'Approve {{no}}',
    approveBody:
      'Approve the order to {{supplier}} for {{total}}. Your name and the time are recorded with the approval.',
    returnTitle: 'Return {{no}}',
    returnBody: 'The order goes back to draft. Write the reason.',
    sendTitle: 'Send {{no}} to the supplier',
    sendBody: 'The order is e-mailed to {{email}} with the PDF attached, within a minute.',
    sendNoEmail:
      'The supplier has no e-mail address. Add it on the supplier card, or print or download the PO, send it yourself and mark it as sent.',
    markSentTitle: 'Mark {{no}} as sent',
    markSentBody:
      'Record that you sent the order to the supplier yourself (printed or downloaded PDF).',
    confirmTitle: 'Supplier confirmation',
    confirmBody: "Record the supplier's order confirmation.",
    supplierRef: 'Confirmation No',
    confirmedDelivery: 'Confirmed Delivery',
    cancelTitle: 'Cancel {{no}}',
    cancelBody: 'A cancelled order cannot be changed. A new order can be created from the RFQ.',
    closeTitle: 'Close {{no}}',
    closeBody: 'Close the order as completed.',
    closeShortBody: 'Part of the order is still open. Write why the rest will not be received.',
    editTitle: 'Delivery details',
    save: 'Save',
    notices: {
      created: 'Purchase order {{no}} created.',
      saved: 'Delivery details saved.',
      submitted: '{{no}} submitted for approval.',
      approved: '{{no}} approved and issued.',
      returned: '{{no}} returned to draft.',
      queued: 'The e-mail is queued and will be sent within a minute.',
      markedSent: '{{no}} marked as sent.',
      confirmed: 'Supplier confirmation recorded.',
      cancelled: '{{no}} cancelled.',
      closed: '{{no}} completed.',
    },
    pdfError: 'The PDF could not be created.',
  },
  pdf: {
    title: 'PURCHASE ORDER',
    date: 'Date',
    supplier: 'Supplier',
    vessel: 'Vessel',
    requisition: 'Requisition',
    currency: 'Currency',
    deliveryLocation: 'Delivery Location',
    expectedDelivery: 'Expected Delivery',
    deliveryTerms: 'Delivery Terms',
    paymentTerms: 'Payment Terms',
    warranty: 'Warranty',
    supplierRef: 'Supplier Ref',
    material: 'Material',
    description: 'Description',
    qty: 'Qty',
    uom: 'UOM',
    unitPrice: 'Unit Price',
    lineTotal: 'Total',
    itemsTotal: 'Items Total',
    discount: 'Discount',
    freight: 'Freight',
    customs: 'Customs',
    tax: 'Tax',
    serviceFee: 'Service Fee',
    total: 'Total',
    remarks: 'Remarks',
    notApproved: 'DRAFT — NOT APPROVED, DO NOT SEND',
    cancelled: 'CANCELLED',
    footer: 'Generated by Felixmate on {{at}}',
    page: 'Page {{page}} of {{pages}}',
    fileName: 'purchase-order',
  },
};

export const poTr: typeof poEn = {
  errors: {
    ROW_VERSION_CONFLICT: 'Siparişi başka biri değiştirdi. Yeniden açıp deneyin.',
    CONTENT_CHANGED: 'Sipariş bu arada değişti. Yeniden açıp deneyin.',
    NOT_PENDING: 'Sipariş onay beklemiyor.',
    SELF_APPROVAL_FORBIDDEN: 'Açtığınız ya da onaya gönderdiğiniz siparişi onaylayamazsınız.',
  },
  statuses: {
    draft: 'Taslak',
    pending_approval: 'Onay Bekliyor',
    issued: 'Onaylandı',
    sent: 'Tedarikçiye Gönderildi',
    confirmed: 'Teyit Edildi',
    partially_delivered: 'Kısmi Teslim',
    delivered: 'Teslim Edildi',
    completed: 'Tamamlandı',
    cancelled: 'İptal Edildi',
  },
  list: {
    title: 'Satın Alma Siparişleri',
    descriptionWeb: 'Seçilen tekliflerden tedarikçiye siparişler; onay ve teslim durumuyla.',
    descriptionShip: 'Ofisin açtığı gemi siparişleri. Malzemeyi GRN ile teslim alın.',
    new: 'Yeni Sipariş',
    filters: 'Sipariş süzgeçleri',
    search: 'Ara',
    searchPlaceholder: 'Sipariş no, tedarikçi ara…',
    vessel: 'Gemi',
    allVessels: 'Bütün Gemiler',
    status: 'Durum',
    allStatuses: 'Bütün Durumlar',
    dateRange: 'Tarih Aralığı',
    dateFrom: 'Sipariş tarihi başı',
    dateTo: 'Sipariş tarihi sonu',
    table: 'Siparişler',
    columns: {
      poNo: 'Sipariş No',
      date: 'Tarih',
      supplier: 'Tedarikçi',
      vessel: 'Gemi',
      amount: 'Tutar',
      status: 'Durum',
      actions: 'İşlemler',
    },
    open: '{{no}} aç',
    showing: '{{total}} kayıttan {{from}}-{{to}}',
    pagination: 'Sipariş sayfaları',
    emptyTitle: 'Henüz sipariş yok.',
    emptyWeb: 'Tedarikçisi seçilmiş bir teklif isteğinden sipariş açın.',
    emptyShip: 'Ofisin açtığı siparişler senkrondan sonra burada görünür.',
    noMatch: 'Süzgeçlere uyan sipariş yok.',
    error: 'Siparişler yüklenemedi.',
    receivedAfterCancel: 'İptalden sonra teslim alındı',
    receivedAfterClose: 'Kapanıştan sonra değişti',
  },
  new: {
    title: 'Yeni Satın Alma Siparişi',
    body: 'Tedarikçisi seçilmiş bir teklif isteği seçin. Kalemler ve fiyatlar seçilen tekliften kopyalanır.',
    rfq: 'Teklif İsteği',
    option: '{{rfq}} · {{supplier}} · {{total}} {{currency}}',
    none: 'Sipariş bekleyen, tedarikçisi seçilmiş teklif isteği yok.',
    create: 'Sipariş Aç',
  },
  detail: {
    title: 'Satın Alma Siparişi',
    description: 'Tedarikçiye sipariş: onay, gönderim ve teslimler.',
    loading: 'Sipariş yükleniyor…',
    error: 'Sipariş yüklenemedi.',
    supplier: 'Tedarikçi',
    currency: 'Para Birimi',
    totalAmount: 'Toplam Tutar',
    expectedDelivery: 'Beklenen Teslim',
    print: 'Yazdır',
    download: 'İndir',
    send: 'Tedarikçiye Gönder',
    resend: 'Yeniden Gönder',
    markSent: 'Gönderildi Olarak İşaretle',
    submit: 'Onaya Gönder',
    approve: 'Onayla',
    return: 'İade Et',
    edit: 'Teslim Bilgisini Düzelt',
    confirm: 'Tedarikçi Teyit Etti',
    cancel: 'Siparişi İptal Et',
    close: 'Siparişi Kapat',
    receive: 'Teslim Al (GRN)',
    back: 'Siparişlere dön',
    tabsLabel: 'Sipariş bölümleri',
    tabs: {
      items: 'Kalemler',
      delivery: 'Teslim Bilgileri',
      receipts: 'Mal Kabulleri ({{count}})',
      history: 'Geçmiş',
    },
    columns: {
      no: '#',
      material: 'Malzeme',
      qty: 'Miktar',
      uom: 'Birim',
      unitPrice: 'Birim Fiyat',
      total: 'Toplam',
      received: 'Teslim Alınan',
      open: 'Açık',
    },
    total: 'Toplam',
    itemsTotal: 'Kalemler',
    discount: 'İndirim',
    freight: 'Navlun',
    customs: 'Gümrük',
    tax: 'Vergi',
    serviceFee: 'Hizmet Bedeli',
    received: 'Teslim Alınan',
    remaining: 'Kalan',
    facts: {
      poNo: 'Sipariş No',
      date: 'Tarih',
      vessel: 'Gemi',
      requisition: 'Talep',
      rfq: 'Teklif İsteği',
      department: 'Bölüm',
      requestedBy: 'Talep Eden',
      deliveryLocation: 'Teslim Yeri',
      deliveryTerms: 'Teslim Şartı',
      paymentTerms: 'Ödeme Şartı',
      warranty: 'Garanti',
      supplierRef: 'Tedarikçi Teyit No',
      remarks: 'Not',
      contact: 'İlgili Kişi',
      email: 'E-posta',
    },
    openRequisition: '{{no}} aç',
    openRfq: '{{no}} aç',
    receiptsEmpty: 'Henüz mal kabulü yok.',
    receiptsEmptyText: 'Mal kabulü gemide bu siparişe karşı girilir.',
    receiptColumns: {
      grnNo: 'GRN No',
      date: 'Tarih',
      receivedBy: 'Teslim Alan',
      deliveryNote: 'İrsaliye',
      accepted: 'Kabul',
      status: 'Durum',
    },
    receiptCancelled: 'İptal',
    receiptValid: 'Teslim Alındı',
    emails: 'Tedarikçiye e-postalar',
    emailColumns: { to: 'Alıcı', queuedAt: 'İstek', status: 'Durum', attempts: 'Deneme' },
    emailStatus: {
      queued: 'Kuyrukta',
      sending: 'Gönderiliyor',
      sent: 'Gönderildi',
      failed: 'Başarısız',
      skipped: 'Atlandı',
    },
    events: {
      created: 'Sipariş açıldı',
      submitted: 'Onaya gönderildi',
      returned: 'Taslağa iade edildi',
      approved: 'Onaylandı',
      sent: 'Tedarikçiye gönderildi',
      confirmed: 'Tedarikçi teyit etti',
      cancelled: 'İptal edildi',
      closed: 'Kapatıldı',
    },
    sentVia: { email: 'e-postayla', manual: 'elle işaretlendi' },
    noHistory: 'Henüz geçmiş yok.',
    draftNote: 'Taslak: teslim bilgilerini denetleyip siparişi onaya gönderin.',
    pendingNote: 'Satın alma onay yetkisi olan başka bir kişinin onayını bekliyor.',
    cancelledNote: 'İptal edildi: {{reason}}',
    closedNote: 'Tamamlandı: {{reason}}',
    closedNoReason: 'Tamamlandı.',
    receivedAfterCancel_one:
      'İptalden sonra gemide mal kabulü girildi ({{count}} kabul). Gemiyle görüşün.',
    receivedAfterCancel_other:
      'İptalden sonra gemide mal kabulü girildi ({{count}} kabul). Gemiyle görüşün.',
    receivedAfterClose_one:
      'Sipariş kapandıktan sonra gemide bir mal kabulü girildi ya da iptal edildi. Gemiyle görüşün.',
    receivedAfterClose_other:
      'Sipariş kapandıktan sonra gemide {{count}} mal kabulü girildi ya da iptal edildi. Gemiyle görüşün.',
    mailOff:
      "E-posta ayarlı değil. PO'yu yazdırın ya da indirin, tedarikçiye kendiniz gönderin ve gönderildi olarak işaretleyin.",
    emailPending:
      'Tedarikçiye giden e-posta kuyrukta. Gittikten sonra sipariş yeniden gönderilebilir.',
    readOnly: 'Sipariş ofiste açılır; gemide salt okunurdur.',
    submitTitle: '{{no}} onaya gönder',
    submitBody:
      'Gönderim adınıza kaydedilir. Sipariş onaylanana ya da iade edilene kadar kilitlidir.',
    approveTitle: '{{no}} onayla',
    approveBody:
      '{{supplier}} için {{total}} tutarındaki siparişi onaylayın. Adınız ve saat onayla kaydedilir.',
    returnTitle: '{{no}} iade et',
    returnBody: 'Sipariş taslağa döner. Gerekçeyi yazın.',
    sendTitle: '{{no}} tedarikçiye gönder',
    sendBody: 'Sipariş PDF ekiyle {{email}} adresine bir dakika içinde e-postayla gider.',
    sendNoEmail:
      "Tedarikçinin e-posta adresi yok. Tedarikçi kartına ekleyin ya da PO'yu yazdırıp indirin, kendiniz gönderin ve gönderildi olarak işaretleyin.",
    markSentTitle: '{{no}} gönderildi olarak işaretle',
    markSentBody:
      'Siparişi tedarikçiye kendiniz gönderdiğinizi kaydedin (yazdırılan ya da indirilen PDF).',
    confirmTitle: 'Tedarikçi teyidi',
    confirmBody: 'Tedarikçinin sipariş teyidini kaydedin.',
    supplierRef: 'Teyit No',
    confirmedDelivery: 'Teyit Edilen Teslim',
    cancelTitle: '{{no}} iptal et',
    cancelBody: 'İptal edilen sipariş değiştirilemez. Teklif isteğinden yeni sipariş açılabilir.',
    closeTitle: '{{no}} kapat',
    closeBody: 'Siparişi tamamlandı olarak kapatın.',
    closeShortBody: 'Siparişin bir kısmı hâlâ açık. Kalanın neden gelmeyeceğini yazın.',
    editTitle: 'Teslim bilgileri',
    save: 'Kaydet',
    notices: {
      created: '{{no}} siparişi açıldı.',
      saved: 'Teslim bilgileri kaydedildi.',
      submitted: '{{no}} onaya gönderildi.',
      approved: '{{no}} onaylandı.',
      returned: '{{no}} taslağa iade edildi.',
      queued: 'E-posta kuyruğa alındı; bir dakika içinde gönderilir.',
      markedSent: '{{no}} gönderildi olarak işaretlendi.',
      confirmed: 'Tedarikçi teyidi kaydedildi.',
      cancelled: '{{no}} iptal edildi.',
      closed: '{{no}} tamamlandı.',
    },
    pdfError: 'PDF oluşturulamadı.',
  },
  // Tedarikçiye giden belge İngilizcedir; Türkçe arayüzde de aynı metinler gösterilir
  pdf: poEn.pdf,
};

export const grnEn = {
  // Ortak kodların mal kabulüne özgü metni (M12c-R20)
  errors: {
    ROW_VERSION_CONFLICT: 'Someone else changed this GRN. Reopen it and try again.',
    EXPIRY_REQUIRED: 'This item requires an expiry date. Enter it, or tick "No expiry date".',
    EXPIRY_CONFLICT: 'A line marked "No expiry date" cannot also have an expiry date.',
    BATCH_REQUIRED: 'This item requires a batch number on receipt.',
  },
  statuses: { completed: 'Completed', partial: 'Partial', cancelled: 'Cancelled' },
  lineStatuses: { ok: 'OK', partial: 'Partial', issue: 'Check' },
  conditions: {
    accepted: 'Accepted',
    accepted_with_remarks: 'Accepted with Remarks',
    rejected: 'Rejected',
    damaged: 'Damaged',
    wrong_item: 'Wrong Item',
    short_delivery: 'Short Delivery',
    certificate_missing: 'Certificate Missing',
    specification_not_met: 'Specification Not Met',
  },
  list: {
    title: 'Goods Receipt',
    descriptionShip:
      'Receive delivered goods against purchase orders; accepted quantities go into stock.',
    descriptionWeb:
      'Goods received on board against purchase orders (entered on board; read only).',
    new: 'New GRN',
    filters: 'GRN filters',
    search: 'Search',
    searchPlaceholder: 'Search by GRN No, PO No…',
    vessel: 'Vessel',
    allVessels: 'All Vessels',
    status: 'Status',
    allStatuses: 'All Statuses',
    dateRange: 'Date Range',
    dateFrom: 'Received date from',
    dateTo: 'Received date to',
    table: 'Goods receipts',
    columns: {
      grnNo: 'GR No',
      date: 'Date',
      poNo: 'PO No',
      supplier: 'Supplier',
      vessel: 'Vessel',
      receivedBy: 'Received By',
      status: 'Status',
    },
    open: 'Open {{no}}',
    showing: 'Showing {{from}}-{{to}} of {{total}}',
    pagination: 'GRN pages',
    emptyTitle: 'No goods received yet.',
    emptyShip: 'Create a GRN when the supplier delivers a purchase order.',
    emptyWeb: 'GRNs entered on board appear here after synchronisation.',
    noMatch: 'No GRN matches the filters.',
    error: 'The goods receipts could not be loaded.',
  },
  detail: {
    title: 'Delivery & GRN',
    description: 'Goods received against the purchase order and booked to stock.',
    loading: 'Loading the GRN…',
    error: 'The GRN could not be loaded.',
    poNo: 'PO No',
    supplier: 'Supplier',
    deliveryNote: 'Delivery Note',
    receivedBy: 'Received By',
    receivedDate: 'Received Date',
    tabsLabel: 'GRN sections',
    tabs: { items: 'Items', details: 'Details' },
    columns: {
      no: '#',
      material: 'Material',
      ordered: 'Ordered',
      received: 'Received',
      damaged: 'Damaged',
      missing: 'Missing',
      uom: 'UOM',
      condition: 'Condition',
      status: 'Status',
    },
    created: 'GRN created',
    createdBy: '{{name}} · {{at}}',
    batch: 'Batch',
    serial: 'Serial',
    expiry: 'Expiry',
    noExpiry: 'No expiry date',
    remarks: 'Remarks',
    stocked: 'Booked to stock',
    nothingBooked: 'Nothing accepted: not booked to stock',
    notStocked: 'Not a catalogue item: not booked to stock',
    // İkame satırı (ADR 0115 §4)
    substitute: 'Substitute: not booked to stock as the requested item',
    cancel: 'Cancel GRN',
    cancelTitle: 'Cancel {{no}}',
    cancelBody:
      'The stock received with this GRN is taken back with a correcting movement. If the material has been used, the GRN cannot be cancelled. Correct by entering a new GRN.',
    cancelledNote: 'Cancelled: {{reason}}',
    openPo: 'Open {{no}}',
    back: 'Back to GRNs',
    notices: {
      created: '{{no}} created; stock updated.',
      cancelled: '{{no}} cancelled; stock corrected.',
    },
  },
  form: {
    title: 'New GRN',
    header: 'Delivery',
    loading: 'Loading the GRN form…',
    error: 'The GRN form could not be loaded.',
    fieldsInvalid: 'Check the highlighted fields.',
    description:
      'Record what was delivered. Accepted quantities go into stock; damaged and missing quantities stay open on the order.',
    order: 'Purchase Order',
    orderPlaceholder: 'Select a purchase order',
    orderOption: '{{no}} · {{supplier}}',
    noOrders: 'There is no purchase order waiting for delivery.',
    receivedDate: 'Received Date',
    deliveryNote: 'Delivery Note',
    receivedBy: 'Received By',
    remarks: 'Remarks',
    items: 'Items',
    columns: {
      material: 'Material',
      ordered: 'Ordered',
      open: 'Open',
      accepted: 'Received',
      damaged: 'Damaged',
      missing: 'Missing',
      condition: 'Condition',
      batch: 'Batch / Serial',
      expiry: 'Expiry',
      noExpiry: 'No expiry date',
    },
    noExpiry: 'No expiry date',
    field: '{{name}} — {{field}}',
    receiveAll: 'Receive All',
    clear: 'Clear',
    save: 'Create GRN',
    cancel: 'Cancel',
    summary: '{{accepted}} of {{open}} open received',
    overOpen: 'More than the open quantity.',
  },
};

export const grnTr: typeof grnEn = {
  errors: {
    ROW_VERSION_CONFLICT: "GRN'yi başka biri değiştirdi. Yeniden açıp deneyin.",
    EXPIRY_REQUIRED:
      'Bu kalemde son kullanma tarihi gerekli. Tarihi girin ya da "Son kullanma tarihi yok" işaretleyin.',
    EXPIRY_CONFLICT: '"Son kullanma tarihi yok" işaretli satıra tarih girilemez.',
    BATCH_REQUIRED: 'Bu kalemin kabulünde parti numarası zorunlu.',
  },
  statuses: { completed: 'Tamamlandı', partial: 'Kısmi', cancelled: 'İptal Edildi' },
  lineStatuses: { ok: 'Tamam', partial: 'Kısmi', issue: 'Denetle' },
  conditions: {
    accepted: 'Kabul',
    accepted_with_remarks: 'Notlu Kabul',
    rejected: 'Red',
    damaged: 'Hasarlı',
    wrong_item: 'Yanlış Kalem',
    short_delivery: 'Eksik Teslim',
    certificate_missing: 'Sertifika Eksik',
    specification_not_met: 'Şartnameye Uymuyor',
  },
  list: {
    title: 'Mal Kabulü',
    descriptionShip:
      'Teslim edilen malzemeyi siparişe karşı kabul edin; kabul edilen miktar stoğa girer.',
    descriptionWeb: 'Gemide siparişlere karşı girilen mal kabulleri (gemide girilir; salt okunur).',
    new: 'Yeni GRN',
    filters: 'GRN süzgeçleri',
    search: 'Ara',
    searchPlaceholder: 'GRN no, sipariş no ara…',
    vessel: 'Gemi',
    allVessels: 'Bütün Gemiler',
    status: 'Durum',
    allStatuses: 'Bütün Durumlar',
    dateRange: 'Tarih Aralığı',
    dateFrom: 'Teslim tarihi başı',
    dateTo: 'Teslim tarihi sonu',
    table: 'Mal kabulleri',
    columns: {
      grnNo: 'GR No',
      date: 'Tarih',
      poNo: 'Sipariş No',
      supplier: 'Tedarikçi',
      vessel: 'Gemi',
      receivedBy: 'Teslim Alan',
      status: 'Durum',
    },
    open: '{{no}} aç',
    showing: '{{total}} kayıttan {{from}}-{{to}}',
    pagination: 'GRN sayfaları',
    emptyTitle: 'Henüz mal kabulü yok.',
    emptyShip: 'Tedarikçi bir siparişi teslim edince GRN açın.',
    emptyWeb: "Gemide girilen GRN'ler senkrondan sonra burada görünür.",
    noMatch: 'Süzgeçlere uyan GRN yok.',
    error: 'Mal kabulleri yüklenemedi.',
  },
  detail: {
    title: 'Teslim ve GRN',
    description: 'Siparişe karşı teslim alınan ve stoğa giren malzeme.',
    loading: 'GRN yükleniyor…',
    error: 'GRN yüklenemedi.',
    poNo: 'Sipariş No',
    supplier: 'Tedarikçi',
    deliveryNote: 'İrsaliye',
    receivedBy: 'Teslim Alan',
    receivedDate: 'Teslim Tarihi',
    tabsLabel: 'GRN bölümleri',
    tabs: { items: 'Kalemler', details: 'Ayrıntılar' },
    columns: {
      no: '#',
      material: 'Malzeme',
      ordered: 'Sipariş',
      received: 'Teslim Alınan',
      damaged: 'Hasarlı',
      missing: 'Eksik',
      uom: 'Birim',
      condition: 'Kalite',
      status: 'Durum',
    },
    created: 'GRN oluşturuldu',
    createdBy: '{{name}} · {{at}}',
    batch: 'Parti',
    serial: 'Seri',
    expiry: 'Son Kullanma',
    noExpiry: 'Son kullanma tarihi yok',
    remarks: 'Not',
    stocked: 'Stoğa girdi',
    nothingBooked: 'Kabul yok: stoğa girmedi',
    notStocked: 'Katalog dışı kalem: stoğa girmez',
    substitute: 'İkame: istenen kalem olarak stoğa girmez',
    cancel: 'GRN İptal Et',
    cancelTitle: '{{no}} iptal et',
    cancelBody:
      'Bu GRN ile stoğa giren miktar düzeltme hareketiyle geri alınır. Malzeme kullanıldıysa GRN iptal edilemez. Düzeltme yeni GRN ile yapılır.',
    cancelledNote: 'İptal edildi: {{reason}}',
    openPo: '{{no}} aç',
    back: "GRN'lere dön",
    notices: {
      created: '{{no}} oluşturuldu; stok güncellendi.',
      cancelled: '{{no}} iptal edildi; stok düzeltildi.',
    },
  },
  form: {
    title: 'Yeni GRN',
    header: 'Teslim',
    loading: 'GRN formu yükleniyor…',
    error: 'GRN formu yüklenemedi.',
    fieldsInvalid: 'İşaretli alanları denetleyin.',
    description:
      'Teslim edileni kaydedin. Kabul edilen miktar stoğa girer; hasarlı ve eksik miktar siparişte açık kalır.',
    order: 'Satın Alma Siparişi',
    orderPlaceholder: 'Sipariş seçin',
    orderOption: '{{no}} · {{supplier}}',
    noOrders: 'Teslim bekleyen sipariş yok.',
    receivedDate: 'Teslim Tarihi',
    deliveryNote: 'İrsaliye',
    receivedBy: 'Teslim Alan',
    remarks: 'Not',
    items: 'Kalemler',
    columns: {
      material: 'Malzeme',
      ordered: 'Sipariş',
      open: 'Açık',
      accepted: 'Teslim Alınan',
      damaged: 'Hasarlı',
      missing: 'Eksik',
      condition: 'Kalite',
      batch: 'Parti / Seri',
      expiry: 'Son Kullanma',
      noExpiry: 'Son kullanma tarihi yok',
    },
    noExpiry: 'Son kullanma tarihi yok',
    field: '{{name}} — {{field}}',
    receiveAll: 'Tamamını Teslim Al',
    clear: 'Temizle',
    save: 'GRN Oluştur',
    cancel: 'Vazgeç',
    summary: '{{open}} açıktan {{accepted}} teslim alındı',
    overOpen: 'Açık miktardan fazla.',
  },
};
